'use client';

import { MouseEvent, useMemo } from 'react';

import { DesktopOutcomeRow, MobileOutcomeRow } from '@/components';
import { useT } from '@/hooks';
import { OutcomeItem } from '@/types';
import { formatProbability, formatVolume } from '@/utils';
import { resolveOutcomeData } from '@/utils/marketUtils';

export interface OutcomeCardProps {
  data: OutcomeItem;
  isSelected: boolean;
  selectedSide?: 'yes' | 'no';
  isExpanded: boolean;
  onExpand: (outcomeId: string) => void;
  onBuyYesClick: () => void;
  onBuyNoClick: () => void;
  activeTab?: 'buy' | 'sell';
}

export function OutcomeCard({
  data,
  isSelected,
  selectedSide,
  onExpand,
  onBuyYesClick,
  onBuyNoClick,
  activeTab = 'buy',
}: OutcomeCardProps) {
  const { t } = useT();

  const resolvedData = useMemo(() => {
    return resolveOutcomeData(data);
  }, [data]);

  const {
    id,
    name,
    price,
    bestAsk,
    bestBid,
    bestAskNo,
    bestBidNo,
    volume,
    image,
    outcomeNameYes,
    outcomeNameNo,
  } = resolvedData;

  function handleExpand() {
    onExpand(id || '');
  }

  function handleBuyYes(e: MouseEvent) {
    e.stopPropagation();
    onBuyYesClick();
  }

  function handleBuyNo(e: MouseEvent) {
    e.stopPropagation();
    onBuyNoClick();
  }

  const formattedVolume = formatVolume(volume);

  const percent = formatProbability(price, 0);
  const yesPrice = (activeTab === 'buy' ? bestAsk * 100 : bestBid * 100).toFixed(1);

  const noPrice = (activeTab === 'buy' ? bestAskNo * 100 : bestBidNo * 100).toFixed(1);

  const actionText = activeTab === 'buy' ? t('trade.buy') : t('trade.sell');

  const localizedOutcomeNameYes =
    outcomeNameYes?.toLowerCase() === 'yes'
      ? t('trade.yes')
      : outcomeNameYes?.toLowerCase() === 'no'
        ? t('trade.no')
        : outcomeNameYes;

  const localizedOutcomeNameNo =
    outcomeNameNo?.toLowerCase() === 'yes'
      ? t('trade.yes')
      : outcomeNameNo?.toLowerCase() === 'no'
        ? t('trade.no')
        : outcomeNameNo;

  const commonProps = {
    name,
    formattedVolume,
    percent,
    outcomeNameYes: `${actionText} ${localizedOutcomeNameYes}`,
    yesPrice,
    outcomeNameNo: `${actionText} ${localizedOutcomeNameNo}`,
    noPrice,
    isSelected,
    selectedSide,
    onExpand: handleExpand,
    onBuyYes: handleBuyYes,
    onBuyNo: handleBuyNo,
  };

  return (
    <>
      <MobileOutcomeRow {...commonProps} />
      <DesktopOutcomeRow {...commonProps} image={image} />
    </>
  );
}
